import { useState, memo } from 'react';
import classnames from 'classnames';
import { AsyncDataHandler } from '@ds';
import { useGetGovernmentCaseNumbers } from 'src/shared/queries/useGetGovernmentCaseNumbers';
import { useGetCaseQuestionnaires } from 'src/shared/queries/useGetCaseQuestionnaires';
import { useSidebarStore } from 'src/stores/SidebarStore';
import { useFormatDate } from 'src/utils/hooks/useFormatDate';
import GovernmentCaseNumbers from './GovernmentCaseNumbers/GovernmentCaseNumbers';
import PrimaryCaseDetails from './PrimaryCaseDetails/PrimaryCaseDetails';
import { ICaseInfo } from 'src/shared/types/Case';
import { Address } from 'src/shared/types/Address';
import { CaseTypesWithTrackingNumber } from 'src/utils/constants/Case';
import { getDependentRelationship } from 'src/utils/services/User';
import Questionnaires from 'src/components/Questionnaires/Questionnaires';
import Processes from './Processes/Processes';
import { CountryCode, FallbackPlaceholderText } from 'src/utils/constants/common';
import styles from './CaseDetails.module.scss';
import { useAidiAccess } from 'src/utils/hooks/useAidiAccess';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'src/utils/constants/FeatureFlags';

const Location = memo((props: { location: Address }) => {
  const { location } = props;
  return (
    <p>
      {location.addressLine1 && <span>{location.addressLine1} </span>}
      {location.addressLine2 && <span>{location.addressLine2} </span>}
      {location.unitType && location.suite && (
        <span>
          {location.unitType} {location.suite}
        </span>
      )}
      {location.city && <span>{location.city}, </span>}
      {location.state && <span>{location.state} </span>}
      {location.country && <span>{location.country} </span>}
      {location.zipCode && <span>{location.zipCode}</span>}
    </p>
  );
});

const PacketTrackingNumber = memo((props: { trackingNumber: string }) => {
  const { trackingNumber } = props;

  if (!trackingNumber) {
    return <p className="info-content">Available at Case Completion</p>;
  }

  const trimmedTrackingNumber = trackingNumber.trim();
  const isFedExTrackingNumber = trimmedTrackingNumber.length === 12;

  return (
    <p>
      {isFedExTrackingNumber ? (
        <a target="_blank" href={`${window.FN_CONFIG.FEDEX_TRACKING_BASE_URL}${trimmedTrackingNumber}`}>
          {trimmedTrackingNumber}
        </a>
      ) : (
        trimmedTrackingNumber
      )}
    </p>
  );
});

export const CaseDetails = (props: { caseDetails: ICaseInfo; workLocations?: Address[] }) => {
  const { caseDetails, workLocations } = props;
  const isRemote = caseDetails.isRemote ?? false;
  const [isExpanded, setIsExpanded] = useState(false);
  const caseId = (isExpanded && caseDetails?.caseId ? caseDetails.caseId : undefined) as number;
  const { data: governmentCaseNumbers, isLoading: isLoadingGovernmentCaseNumbers } =
    useGetGovernmentCaseNumbers(caseId);
  const { isCollapsed: isSidenavCollapsed } = useSidebarStore();
  const isCaseActive = caseDetails?.isActive ?? false;
  const { data: questionnaires, isLoading: isLoadingQuestionnaires } = useGetCaseQuestionnaires(
    isCaseActive ? caseId : 0
  );
  const { shouldDisableLegacy: hideLegacyQuestionaireOrDocuments, loading: loadingAidiAccess } = useAidiAccess({
    caseId: caseDetails?.caseId || null,
    flagOnly: true,
  });
  const flags = useFlags();

  if (!caseDetails) {
    return null;
  }

  const onToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const caseSubTypes =
    caseDetails?.associatedCaseSubTypes?.map(({ caseSubType }: { caseSubType: string }) => caseSubType).join(', ') ||
    FallbackPlaceholderText.NotAvailable;
  const dependentsFullName =
    caseDetails?.dependents?.map(({ dependentFullName }) => dependentFullName).join(', ') || '';
  const relationship = getDependentRelationship(caseDetails?.dependents, caseDetails?.isDependentOnly ?? undefined);
  const caseOpenDate = useFormatDate(caseDetails?.createdDate);
  const hasHiddenAttorneyData = !!flags[FeatureFlags.HideLegalContacts];

  return (
    <div className={`${styles['case-details']} ${isExpanded ? styles['case-details-expanded'] : ''}`}>
      <div className={styles['caret-icon-wrapper']} onClick={onToggle}>
        <div className={`${styles['caret-icon']} ${isExpanded ? styles['caret-icon-up'] : ''}`}></div>
      </div>

      <div
        className={classnames(styles['primary-info-row'], {
          [styles['primary-info-row-collapsed-state']]: !isExpanded,
          [styles['primary-info-row-expanded-side-panel-state']]: !isSidenavCollapsed,
        })}
        onClick={onToggle}
      >
        <PrimaryCaseDetails
          employeeName={caseDetails.employeeName || ''}
          caseOpenDate={caseOpenDate}
          relationship={relationship}
          dependentsFullName={dependentsFullName}
          employeeID={caseDetails.employeeID as string}
          isExpanded={isExpanded}
          caseSubTypes={caseSubTypes}
          companyName={caseDetails.companyName || ''}
        />
      </div>

      {isExpanded && (
        <>
          <div className={styles['secondary-info-row']}>
            <div>
              <AsyncDataHandler isLoading={isLoadingGovernmentCaseNumbers}>
                {governmentCaseNumbers && governmentCaseNumbers.length > 0 && (
                  <GovernmentCaseNumbers
                    caseCountryCode={caseDetails.caseCountryCode}
                    governmentCaseNumbers={governmentCaseNumbers}
                  />
                )}
              </AsyncDataHandler>

              {!hasHiddenAttorneyData && (
                <>
                  <p className={styles['case-content']}>
                    <span className={styles['info-label']}>Case Assistant (Primary)</span>
                    <span className={styles['info-value']}>
                      {caseDetails.primaryAssistantName || FallbackPlaceholderText.NotAvailable}
                    </span>
                  </p>
                  <p className={styles['case-content']}>
                    <span className={styles['info-label']}>Case Attorney</span>
                    <span className={styles['info-value']}>
                      {caseDetails.primaryAttorneyName || FallbackPlaceholderText.NotAvailable}
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
          <div className={styles['case-secondary-info-container']}>
            <div className={styles['case-more-information']}>
              <div className={classnames(styles['case-more-information-card'], styles['case-content'])}>
                <div className={styles['title']}>
                  <span>Case details</span>
                </div>
                <div className={styles['case-detail-row']}>
                  <label>Job Title</label>
                  <p>{caseDetails.jobPosition || FallbackPlaceholderText.NotAvailable}</p>
                </div>
                <div className={styles['case-detail-row']}>
                  <label>Work Location(s)</label>
                  <div className={styles['work-locations']}>
                    {caseDetails.workLocations && caseDetails.workLocations.length > 0 ? (
                      caseDetails.workLocations.map((location: Address, index: number) => (
                        <Location key={index} location={location} />
                      ))
                    ) : workLocations && workLocations.length > 0 ? (
                      workLocations.map((location: Address, index: number) => (
                        <Location key={index} location={location} />
                      ))
                    ) : (
                      <p>{FallbackPlaceholderText.NotAvailable}</p>
                    )}
                  </div>
                </div>
                {caseDetails.caseType &&
                  caseDetails.caseCountryCode == CountryCode.UnitedStates &&
                  CaseTypesWithTrackingNumber.includes(caseDetails.caseType) && (
                    <div className={styles['case-detail-row']}>
                      <label>Packet Tracking Number</label>
                      <PacketTrackingNumber trackingNumber={caseDetails.trackingNumber || ''} />
                    </div>
                  )}
              </div>

              {caseId && isCaseActive && (
                <>
                  <AsyncDataHandler
                    isLoading={isLoadingQuestionnaires || loadingAidiAccess}
                    loader={{ width: '200px', height: '32px' }}
                  >
                    {!hideLegacyQuestionaireOrDocuments && questionnaires && questionnaires.length > 0 && (
                      <div className={styles['case-questionnaires-container']}>
                        <p className={styles['case-questionnaire-title']}>Questionnaires</p>
                        <Questionnaires
                          caseId={caseId}
                          remoteCaseId={caseDetails.remoteCaseId}
                          questionnaires={questionnaires}
                          isRemote={isRemote}
                        />
                      </div>
                    )}
                  </AsyncDataHandler>
                  {!isRemote && <Processes caseId={caseId} />}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
